import React, {useEffect, useMemo, useRef, useState} from "react";
import LabShell from "./LabShell";
import {MISSIONS, SAMPLE_RATE, createTransmission, receiveTransmission, spectrum} from "./signalEngine";
import "./SignalDecoder.scss";

function linePath(values, width, height, scale = 1) {
  return values.map((value, index) => `${index === 0 ? "M" : "L"}${(index / (values.length - 1) * width).toFixed(1)},${(height - Math.min(1, Math.max(0, value * scale)) * height).toFixed(1)}`).join(" ");
}

export default function SignalDecoder() {
  const [missionIndex, setMissionIndex] = useState(0);
  const [frequency, setFrequency] = useState(350);
  const [bandwidth, setBandwidth] = useState(260);
  const [averaging, setAveraging] = useState(1);
  const [recoveredMissions, setRecoveredMissions] = useState([]);
  const [showHint, setShowHint] = useState(false);
  const [listening, setListening] = useState(false);
  const [audioError, setAudioError] = useState("");
  const audioRef = useRef(null);
  const mission = MISSIONS[missionIndex];
  const transmission = useMemo(() => createTransmission(mission), [mission]);
  const spectrumPoints = useMemo(() => spectrum(transmission.captures[0]), [transmission]);
  const reception = useMemo(() => receiveTransmission(transmission, mission, frequency, bandwidth, averaging), [transmission, mission, frequency, bandwidth, averaging]);
  const saved = recoveredMissions.indexOf(mission.id) !== -1;
  const plotX = value => (value - 240) / 720 * 800;
  const bandStart = Math.max(0, plotX(frequency - bandwidth / 2));
  const bandEnd = Math.min(800, plotX(frequency + bandwidth / 2));
  const wave = Array.from({length: 480}, (_, index) => reception.envelope[128 + index * 2]);

  useEffect(() => () => {
    if (audioRef.current) audioRef.current.close().catch(() => {});
    audioRef.current = null;
  }, []);

  const stopAudio = () => {
    if (audioRef.current) audioRef.current.close().catch(() => {});
    audioRef.current = null;
    setListening(false);
  };

  const listen = async () => {
    if (listening) { stopAudio(); return; }
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) { setAudioError("Audio is unavailable in this browser. The visual decoder still works."); return; }
    try {
      const context = new AudioContext();
      audioRef.current = context;
      setListening(true);
      await context.resume();
      // A mission change or unmount may close the context during resume.
      if (audioRef.current !== context) return;
      const samples = transmission.captures[0];
      // AudioBuffer supports standard audio rates; resample the simulation.
      const buffer = context.createBuffer(1, Math.floor(samples.length * context.sampleRate / SAMPLE_RATE), context.sampleRate);
      const channel = buffer.getChannelData(0);
      for (let i = 0; i < channel.length; i++) {
        const position = i * SAMPLE_RATE / context.sampleRate;
        const left = Math.floor(position);
        const fraction = position - left;
        channel[i] = samples[left] * (1 - fraction) + samples[Math.min(left + 1, samples.length - 1)] * fraction;
      }
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      const gain = context.createGain();
      gain.gain.value = .035;
      source.connect(gain);
      gain.connect(context.destination);
      source.start();
      setAudioError("");
      setListening(true);
    } catch (error) {
      stopAudio();
      setAudioError("Audio could not start. Continue using the spectrum and receiver controls.");
    }
  };

  const selectMission = index => {
    stopAudio();
    setMissionIndex(index);
    setFrequency(350);
    setBandwidth(260);
    setAveraging(1);
    setShowHint(false);
    setAudioError("");
  };

  const status = saved ? "TRANSMISSION ARCHIVED" : reception.locked ? "PACKET VERIFIED" : "ACQUIRING SIGNAL";

  return (
    <LabShell eyebrow="MISSION LAB / 03" title="Signal Decoder" intro="Somewhere in the static, someone is trying to reach you. Tune in, clear the noise, and find out what they have to say.">
      <div className="decoder-workbench">
        <div className="decoder-missions" role="group" aria-label="Choose a transmission">
          {MISSIONS.map((item, index) => (
            <button key={item.id} aria-pressed={index === missionIndex} onClick={() => selectMission(index)}>
              <span>{item.tag}</span>
              <strong>{item.name}</strong>
              <b>{recoveredMissions.indexOf(item.id) !== -1 ? "✓ RECOVERED" : "↗ INTERCEPT"}</b>
            </button>
          ))}
        </div>

        <div className="decoder-layout">
          <section className="decoder-display" aria-label="Signal monitor">
            <div className="decoder-monitor-head">
              <span><i /> DEEP SPACE RECEIVER</span>
              <button onClick={listen} aria-pressed={listening}>{listening ? "◼ Stop audio" : "♫ Listen to static"}</button>
            </div>
            {audioError && <p className="decoder-audio-error" role="status">{audioError}</p>}
            <div className="decoder-spectrum-label"><span>INCOMING SPECTRUM</span><span>240—960 Hz</span></div>
            <div className="decoder-spectrum">
              <svg viewBox="-12 -15 824 215" role="img" aria-label={`Incoming radio spectrum. Receiver centered at ${frequency} hertz with ${bandwidth} hertz bandwidth.`}>
                {[0, 40, 80, 120, 160].map(y => <line key={`y${y}`} x1="0" x2="800" y1={y} y2={y} className="decoder-grid-line" />)}
                {[240, 360, 480, 600, 720, 840, 960].map(value => <g key={value}><line x1={plotX(value)} x2={plotX(value)} y1="0" y2="160" className="decoder-grid-line" /><text x={plotX(value)} y="187" textAnchor="middle">{value}</text></g>)}
                <rect x={bandStart} y="0" width={Math.max(0, bandEnd - bandStart)} height="160" className="decoder-passband" />
                <path d={linePath(spectrumPoints, 800, 160, .9)} className="decoder-spectrum-trace" />
                <line x1={plotX(frequency)} x2={plotX(frequency)} y1="0" y2="160" className="decoder-tuning-line" />
              </svg>
            </div>
            <div className="decoder-legend"><span><i className="trace-key" /> Incoming energy</span><span><i className="band-key" /> Receiver passband</span></div>
            <div className="decoder-wave-head"><span>FILTERED ENVELOPE / FIRST 15 BITS</span><span>DECISION LEVEL: 0.27</span></div>
            <svg className="decoder-wave" viewBox="0 0 800 85" role="img" aria-label="Filtered signal envelope and bit decision threshold">
              <line x1="0" x2="800" y1={85 - .27 * 85 * 1.6} y2={85 - .27 * 85 * 1.6} className="decoder-threshold" />
              <path d={linePath(wave, 800, 85, 1.6)} className="decoder-envelope-trace" />
            </svg>
            <div className="decoder-packet-head"><span className={reception.locked || saved ? "decoder-status is-locked" : "decoder-status"}>{status}</span><span>{saved ? mission.message.length : reception.recovered}/{mission.message.length} BYTES</span></div>
            <div className="decoder-message" aria-label="Recovered message preview">{saved ? mission.message : reception.text}</div>
            <p className="decoder-packet-note">{saved ? "Filed in the mission log. Ground control has questions." : "Dots mark bytes that failed verification. Keep tuning until the full packet is clear."}</p>
          </section>

          <section className="decoder-controls" aria-label="Receiver controls">
            <div className="decoder-control-title"><span>OPERATOR CONSOLE</span><strong>Find the signal.</strong></div>
            <label className="decoder-knob" htmlFor="decoder-frequency"><span>01 / CENTER FREQUENCY <output>{frequency} <small>Hz</small></output></span>
              <input id="decoder-frequency" type="range" min="240" max="960" step="1" value={frequency} onChange={event => setFrequency(Number(event.target.value))} />
              <small>Align the receiver with the transmission’s carrier peak.</small>
            </label>
            <label className="decoder-knob" htmlFor="decoder-bandwidth"><span>02 / BANDWIDTH <output>{bandwidth} <small>Hz</small></output></span>
              <input id="decoder-bandwidth" type="range" min="24" max="400" step="2" value={bandwidth} onChange={event => setBandwidth(Number(event.target.value))} />
              <small>Too wide admits interference. Too narrow blurs the bits.</small>
            </label>
            <label className="decoder-knob" htmlFor="decoder-averaging"><span>03 / CAPTURE AVERAGING <output>{averaging}<small>×</small></output></span>
              <input id="decoder-averaging" type="range" min="1" max="8" step="1" value={averaging} onChange={event => setAveraging(Number(event.target.value))} />
              <small>Stack repeated captures to reduce random noise.</small>
            </label>
            <div className="decoder-integrity"><div><span>BIT INTEGRITY</span><strong>{reception.integrity}%</strong></div><progress max="100" value={reception.integrity} aria-label="Bit integrity" /><p>Every byte must pass verification before you can archive the packet.</p></div>
            <button className="decoder-recover" disabled={!reception.locked || saved} onClick={() => setRecoveredMissions(previous => previous.indexOf(mission.id) === -1 ? [...previous, mission.id] : previous)}>{saved ? "✓ Transmission archived" : reception.locked ? "Decode & archive ↗" : "Awaiting a clean packet"}</button>
            <div className="decoder-feedback" role="status" aria-live="polite">{saved ? `${recoveredMissions.length} of 3 transmissions recovered.${recoveredMissions.length === 3 ? " All channels clear. Excellent work, operator." : " Select another channel above."}` : reception.locked ? "Signal locked. The full message is ready to archive." : "Search the spectrum, then adjust bandwidth and averaging."}</div>
            <button className="decoder-hint-toggle" aria-expanded={showHint} aria-controls="decoder-hint" onClick={() => setShowHint(value => !value)}>{showHint ? "− Hide operator hint" : "+ Need an operator hint?"}</button>
            {showHint && <p id="decoder-hint" className="decoder-hint">Try {mission.frequency} Hz, a bandwidth near 140 Hz, and 8× averaging. Once locked, see how few captures you can get away with.</p>}
          </section>
        </div>

        <div className="decoder-underbar"><span>{mission.source}</span><span>{recoveredMissions.length} / 3 TRANSMISSIONS RECOVERED</span></div>
        <details className="decoder-explainer"><summary>What’s happening inside the receiver?</summary><p>This is a synthetic AM transmission: each ASCII bit changes the strength of a carrier wave. The receiver mixes that signal down to baseband, applies a Butterworth low-pass filter, and measures its envelope to decide between 0 and 1. Averaging combines up to eight repeated captures with independent noise. The spectrum shows the first raw capture; the envelope shows your filtered result.</p><p>Frame timing is already synchronized for this experiment. Bit integrity compares the decoded bits with the known training packet; dots mark incorrect bytes. These are simulated audio-frequency channels, with neighboring tones added as interference.</p></details>
      </div>
    </LabShell>
  );
}
